'use client'

/* eslint-disable react-refresh/only-export-components */
import { BankIdStatus, RfaCode } from '@backend/integrations/auth/bankid/types'
import { useQuery } from '@tanstack/react-query'
import { signInWithCustomToken } from 'firebase/auth'
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  useAuthServicePostV1BankidAuth,
  useAuthServicePostV1BankidCollect,
} from '@/api/client/queries'
import { PostV1BankidAuthResponse } from '@/api/client/requests'

import { firebaseAuth } from '../firebase'

type BankIdContextState = {
  autoStartToken?: string
  cancelAuthSession(): void
  isError?: boolean
  launchApp(autoStartToken?: string): void
  orderRef?: string
  pollDurationPercentage: number
  qrCode?: string
  rfaCode?: RfaCode
  startAuthSession(): Promise<void>
  status?: BankIdStatus
}

const BankIdContext = createContext<BankIdContextState>({
  cancelAuthSession: () => {},
  isError: false,
  launchApp: () => {},
  pollDurationPercentage: 100,
  startAuthSession: async () => {},
})

export const useBankId = () => useContext(BankIdContext)

/**
 * Maximum number of BankID authentication sessions to start automatically
 * after a failure before requiring user interaction.
 * Each session lasts for 30 seconds.
 */
const maxAuthSessions = 2

/**
 * Duration of a single BankID authentication session in milliseconds.
 */
const authSessionDuration = 30_000

export const BankIdProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation()
  const [authResponse, setAuthResponse] = useState<PostV1BankidAuthResponse>()
  const [authResponseUpdatedAt, setAuthResponseUpdatedAt] = useState<number>()
  const reconnectionCountRef = useRef(0)
  const bankIdAuth = useAuthServicePostV1BankidAuth()
  const bankIdCollect = useAuthServicePostV1BankidCollect()
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const {
    data: collectData,
    dataUpdatedAt: collectDataUpdatedAt,
    isError: isCollectError,
  } = useQuery({
    enabled: !!authResponse?.orderRef,
    queryFn: () => bankIdCollect.mutateAsync({ requestBody: { orderRef: authResponse!.orderRef } }),
    queryKey: ['bankid-collect', authResponse?.orderRef],
    refetchInterval: ({ state: { data } }) => {
      if (data?.status !== 'pending') return false

      return 1000
    },
    retry: false,
  })

  useEffect(() => {
    if (collectData?.status === 'complete') {
      signInWithCustomToken(firebaseAuth, collectData.token)
        .then(res => {
          console.log('Signed in!', res)
        })
        .catch(err => {
          console.error('Failed to sign in with custom token', err)
        })
    }

    if (collectData?.status === 'failed') {
      if (reconnectionCountRef.current + 1 < maxAuthSessions) {
        reconnectionCountRef.current += 1
        updateAuthSession()
      } else {
        setHasTimedOut(true)
      }
    }
  }, [collectData?.status])

  const updateAuthSession = async () => {
    try {
      const authResponse = await bankIdAuth.mutateAsync()
      setAuthResponseUpdatedAt(Date.now())
      setAuthResponse(authResponse)
    } catch (error) {
      console.error(error)
      setTimeout(() => toast.error(t('bankIdLoginPage.startFailedToast')))
    }
  }

  const startAuthSession = () => {
    reconnectionCountRef.current = 0
    setAuthResponse(undefined)
    setAuthResponseUpdatedAt(undefined)
    setHasTimedOut(false)

    return updateAuthSession()
  }

  const cancelAuthSession = () => {
    reconnectionCountRef.current = 0
    setAuthResponse(undefined)
    setAuthResponseUpdatedAt(undefined)
    setHasTimedOut(false)
  }

  const launchApp = () => openBankIdApp(authResponse?.autoStartToken)

  const pollDurationMs = useMemo(() => {
    if (!authResponseUpdatedAt || !collectDataUpdatedAt) return 0

    return Math.max(0, collectDataUpdatedAt - authResponseUpdatedAt)
  }, [authResponseUpdatedAt, collectDataUpdatedAt])

  const pollDurationPercentage = useMemo(() => {
    if (pollDurationMs === undefined) return 100

    const totalDurationMs = authSessionDuration * maxAuthSessions
    const remainingMs =
      totalDurationMs - authSessionDuration * reconnectionCountRef.current - pollDurationMs

    return (remainingMs / totalDurationMs) * 100
  }, [pollDurationMs])

  const qrCode = collectData && 'qrCode' in collectData ? collectData.qrCode : authResponse?.qrCode
  const rfaCode = collectData && 'rfaCode' in collectData ? collectData.rfaCode : undefined

  return (
    <BankIdContext.Provider
      value={{
        autoStartToken: authResponse?.autoStartToken,
        cancelAuthSession,
        isError: isCollectError || hasTimedOut,
        launchApp,
        orderRef: authResponse?.orderRef,
        pollDurationPercentage,
        qrCode,
        rfaCode: rfaCode as RfaCode | undefined,
        startAuthSession,
        status: collectData?.status as BankIdStatus | undefined,
      }}
    >
      {children}
    </BankIdContext.Provider>
  )
}

const openBankIdApp = (autoStartToken?: string) => {
  if (!autoStartToken) {
    return console.warn('{autoStartToken} is required to open BankID app')
  }

  const link = document.createElement('a')
  link.referrerPolicy = 'origin'
  const searchParams = new URLSearchParams()
  searchParams.append('autostarttoken', autoStartToken)
  searchParams.append('redirect', 'null')

  try {
    link.href = `bankid:///?${searchParams}`
    link.click()
  } catch (error) {
    console.warn('Failed to open BankID app, fallbacking to "app.bankid.com"', error)
    link.href = `https://app.bankid.com?${searchParams}`
    link.click()
  }
}
